import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  EnrichRequest,
  EnrichResponse,
  EnrichmentStatus,
  InsightsApiResponse,
  TagRecommendation,
  WorkflowExample,
} from "../types/insights";

interface InsightsStore {
  // State
  enrichmentId: string | null;
  status: EnrichmentStatus | "idle";
  data: EnrichResponse | null;
  error: string | null;
  lastEnrichedAt: number | null;

  // Cached derived arrays (referentially stable until data changes)
  chainSuggestions: number[];
  tagRecommendations: TagRecommendation[];
  workflowExamples: WorkflowExample[];

  // Actions
  startEnrichment: (request: EnrichRequest) => Promise<void>;
  pollEnrichment: (enrichmentId: string) => void;
  setData: (data: EnrichResponse) => void;
  reset: () => void;

  // Selectors (kept for backward compat, now read cached state)
  getChainSuggestions: () => number[];
  getTagRecommendations: () => TagRecommendation[];
  getWorkflowExamples: () => WorkflowExample[];
  isLoading: () => boolean;
}

// Stable empty arrays — reused to avoid creating new references
const EMPTY_NUMBERS: number[] = [];
const EMPTY_TAGS: TagRecommendation[] = [];
const EMPTY_EXAMPLES: WorkflowExample[] = [];

/** Extract cached derived arrays from enrichment data */
function deriveCachedArrays(data: EnrichResponse | null) {
  return {
    chainSuggestions: data?.recommendations?.chainSuggestions ?? EMPTY_NUMBERS,
    tagRecommendations: data?.recommendations?.tagRecommendations ?? EMPTY_TAGS,
    workflowExamples: data?.recommendations?.workflowExamples ?? EMPTY_EXAMPLES,
  };
}

// Polling state (outside store to avoid serialization)
let pollIntervalId: ReturnType<typeof setInterval> | null = null;
let pollAttempts = 0;
const MAX_POLL_ATTEMPTS = 60; // 60 seconds max
const POLL_INTERVAL = 1000; // 1 second

export const useInsightsStore = create<InsightsStore>()(
  persist(
    (set, get) => ({
      enrichmentId: null,
      status: "idle",
      data: null,
      error: null,
      lastEnrichedAt: null,

      // Cached derived arrays (stable references)
      chainSuggestions: EMPTY_NUMBERS,
      tagRecommendations: EMPTY_TAGS,
      workflowExamples: EMPTY_EXAMPLES,

      startEnrichment: async (request: EnrichRequest) => {
        // Clear any existing poll
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
        pollAttempts = 0;

        set({ status: "processing", error: null });

        try {
          const res = await fetch("/api/insights/enrich", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(request),
          });

          const result: InsightsApiResponse = await res.json();

          if (!result.success) {
            set({ status: "idle", error: result.error || "Enrichment failed" });
            return;
          }

          // If we got immediate data (cached), set it directly
          if (result.data) {
            set({
              status: "completed",
              data: result.data,
              enrichmentId: result.data.enrichmentId || null,
              lastEnrichedAt: Date.now(),
              ...deriveCachedArrays(result.data),
            });
            return;
          }

          // 202 response - start polling
          if (result.enrichmentId && result.status === "processing") {
            set({ enrichmentId: result.enrichmentId, status: "processing" });
            get().pollEnrichment(result.enrichmentId);
          }
        } catch (err) {
          console.error("@@insightsStore:startEnrichment:error", err);
          set({ status: "idle", error: "Failed to start enrichment" });
        }
      },

      pollEnrichment: (enrichmentId: string) => {
        // Clear any existing poll
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
        }
        pollAttempts = 0;

        const poll = async () => {
          pollAttempts++;

          if (pollAttempts > MAX_POLL_ATTEMPTS) {
            clearInterval(pollIntervalId!);
            pollIntervalId = null;
            set({ status: "idle", error: "Enrichment timed out" });
            return;
          }

          try {
            const res = await fetch(`/api/insights/enrich/${enrichmentId}`);
            const result: InsightsApiResponse = await res.json();

            if (!result.success) {
              // Keep polling on transient errors
              if (pollAttempts > 5) {
                console.warn("@@insightsStore:poll:error", result.error);
              }
              return;
            }

            const status = result.status as EnrichmentStatus;

            if (status === "completed" && result.data) {
              clearInterval(pollIntervalId!);
              pollIntervalId = null;
              set({
                status: "completed",
                data: result.data,
                lastEnrichedAt: Date.now(),
                ...deriveCachedArrays(result.data),
              });
            } else if (status === "failed") {
              clearInterval(pollIntervalId!);
              pollIntervalId = null;
              set({
                status: "idle",
                error: result.error || "Enrichment failed",
              });
            }
            // Otherwise keep polling (pending/processing)
          } catch (err) {
            console.error("@@insightsStore:poll:error", err);
            // Keep polling on network errors
          }
        };

        // Start polling immediately, then every interval
        poll();
        pollIntervalId = setInterval(poll, POLL_INTERVAL);
      },

      setData: (data: EnrichResponse) => {
        set({
          status: "completed",
          data,
          enrichmentId: data.enrichmentId || null,
          lastEnrichedAt: Date.now(),
          ...deriveCachedArrays(data),
        });
      },

      reset: () => {
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
        set({
          enrichmentId: null,
          status: "idle",
          data: null,
          error: null,
          lastEnrichedAt: null,
          chainSuggestions: EMPTY_NUMBERS,
          tagRecommendations: EMPTY_TAGS,
          workflowExamples: EMPTY_EXAMPLES,
        });
      },

      // Selectors (read cached state for referential stability)
      getChainSuggestions: () => get().chainSuggestions,
      getTagRecommendations: () => get().tagRecommendations,
      getWorkflowExamples: () => get().workflowExamples,

      isLoading: () => {
        const { status } = get();
        return status === "processing" || status === "pending";
      },
    }),
    {
      name: "b3os-insights",
      // Only persist raw data — cached arrays are re-derived on rehydration
      partialize: state => ({
        data: state.data,
        lastEnrichedAt: state.lastEnrichedAt,
      }),
      onRehydrateStorage: () => state => {
        if (state?.data) {
          const derived = deriveCachedArrays(state.data);
          state.chainSuggestions = derived.chainSuggestions;
          state.tagRecommendations = derived.tagRecommendations;
          state.workflowExamples = derived.workflowExamples;
        }
      },
    },
  ),
);
